import { getEventSenderMetadata, type Logger } from '@interactiveshell/base-utils';
import type { ThemeManager } from '@interactiveshell/color-scheme-manager';
import { commandHandlers } from '@interactiveshell/command-handlers';
import { interactiveShellAppCommands } from '@interactiveshell/commands';
import type { Services } from '@interactiveshell/context';
import type { EditorSaveCoordinator } from '@interactiveshell/editor';

import type {
  BaseServiceCommonOptions,
  RootEmitter,
  Store,
} from '@interactiveshell/types';
import { initializeServices as initializeServices2 } from './initialize-services';

export {
  createEditorSaveCoordinator,
  type EditorSaveCoordinator,
} from '@interactiveshell/editor';
export { readEditorEngineFromUrl } from './initialize-services';

export function initializeServices(
  logger: Logger,
  rootEmitter: RootEmitter,
  store: Store,
  theme: ThemeManager,
  abortSignal: AbortSignal,
  editorSaveCoordinator: EditorSaveCoordinator,
): Promise<Services> {
  const commonOpts: BaseServiceCommonOptions = {
    logger,
    store,
    rootAbortSignal: abortSignal,
    emitAppError(error) {
      rootEmitter.emit('event::error:uncaught-error', {
        error,
        isAppError: true,
        isRejection: false,
        isFakeThrow: true,
        sender: getEventSenderMetadata({ tag: 'initialize-service' }),
      });
    },
  };

  const services = initializeServices2(
    commonOpts,
    rootEmitter,
    interactiveShellAppCommands,
    commandHandlers,
    theme,
    editorSaveCoordinator,
  );

  return services.mountAll().then(() => {
    const core = services.coreServices;

    // R4 feasibility certification only. The hook is deliberately query-gated
    // so no Live Draft control or service surface is exposed during normal use.
    // It lets the release E2E suite exercise the real browser BusyTeX worker
    // and inspect only the structured probe result.
    if (
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('interactiveShellLiveDraftProbe') ===
        '1'
    ) {
      const target = window as unknown as {
        __interactiveShellLiveDraftProbe?: {
          probe: (wsName: string) => ReturnType<typeof core.projectLiveDraft.probe>;
          cancel: () => void;
        };
      };
      target.__interactiveShellLiveDraftProbe = {
        probe: (wsName) => core.projectLiveDraft.probe(wsName),
        cancel: () => core.projectLiveDraft.cancel(),
      };
    }

    return { core };
  });
}
